import React, { useContext, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import MoreHorizOutlined from '@mui/icons-material/MoreHorizOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { AuthContext } from '../../contexts/AuthContext';
import Modal, { ConfirmDialog } from '../ui/Modal';
import { primaryNavItems, secondaryNavItems, activeNavPath } from './navConfig';

/**
 * Mobile module switcher. Before this the only way between modules on a phone
 * was the browser back button.
 *
 * Fixed to the bottom because that is where a thumb reaches on a phone held
 * one-handed — which is how this gets used on site. Every tab clears 44px and
 * the bar pads itself past the iPhone home indicator via `pb-safe`.
 */
function BottomNav() {
  const { isLoggedIn, role, username, logOut } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  if (!isLoggedIn) return null;

  const tabs = primaryNavItems(role);
  const overflow = secondaryNavItems(role);
  const active = activeNavPath(location.pathname);
  const moreIsActive = overflow.some((item) => item.to === active);

  const tabClasses = (isActive) =>
    [
      'flex min-h-11 flex-1 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-md px-1 py-1.5',
      'transition-colors duration-fast ease-out',
      isActive ? 'text-accent' : 'text-muted-fg hover:text-foreground',
    ].join(' ');

  const handleLogOut = async () => {
    setConfirmLogout(false);
    setSheetOpen(false);
    await logOut();
    navigate('/login');
  };

  return (
    <>
      <nav
        aria-label="Primary mobile"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface/95 pb-safe shadow-lg backdrop-blur lg:hidden"
      >
        <div className="flex items-stretch gap-1 px-2 py-1.5">
          {tabs.map(({ to, label, icon: Icon }) => {
            const isActive = active === to;
            return (
              <Link
                key={to}
                to={to}
                aria-current={isActive ? 'page' : undefined}
                className={tabClasses(isActive)}
              >
                {/* The pill behind the icon marks the active tab without relying on colour alone. */}
                <span
                  className={`flex h-7 w-12 items-center justify-center rounded-full transition-colors duration-fast ${
                    isActive ? 'bg-accent-soft' : ''
                  }`}
                >
                  <Icon style={{ fontSize: 22 }} aria-hidden="true" />
                </span>
                <span className="text-[0.6875rem] font-medium leading-none">{label}</span>
              </Link>
            );
          })}

          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            aria-expanded={sheetOpen}
            aria-label="More sections"
            className={tabClasses(moreIsActive)}
          >
            <span
              className={`flex h-7 w-12 items-center justify-center rounded-full transition-colors duration-fast ${
                moreIsActive ? 'bg-accent-soft' : ''
              }`}
            >
              <MoreHorizOutlined style={{ fontSize: 22 }} aria-hidden="true" />
            </span>
            <span className="text-[0.6875rem] font-medium leading-none">More</span>
          </button>
        </div>
      </nav>

      <Modal
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="All sections"
        subtitle={username ? `Signed in as ${username}` : undefined}
        size="sm"
      >
        <div className="-mx-1 space-y-1">
          {overflow.map(({ to, label, description, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setSheetOpen(false)}
              className={`flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 transition-colors duration-fast ${
                active === to ? 'bg-accent-soft' : 'hover:bg-muted'
              }`}
            >
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                  active === to ? 'bg-accent text-accent-fg' : 'bg-muted text-muted-fg'
                }`}
              >
                <Icon style={{ fontSize: 20 }} aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-foreground">{label}</span>
                {description && (
                  <span className="block truncate text-xs text-muted-fg">{description}</span>
                )}
              </span>
              <ChevronRight style={{ fontSize: 20 }} className="shrink-0 text-muted-fg" />
            </Link>
          ))}

          <button
            type="button"
            onClick={() => setConfirmLogout(true)}
            className="mt-2 flex min-h-11 w-full cursor-pointer items-center gap-3 rounded-lg border-t border-border px-3 py-2.5 text-left transition-colors duration-fast hover:bg-danger-soft"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-danger-soft text-danger">
              <LogoutOutlined style={{ fontSize: 20 }} aria-hidden="true" />
            </span>
            <span className="text-sm font-medium text-danger">Log out</span>
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirmLogout}
        onClose={() => setConfirmLogout(false)}
        onConfirm={handleLogOut}
        title="Log out"
        message="You will need to sign in again to access your reports."
        confirmLabel="Log out"
      />
    </>
  );
}

export default BottomNav;
